import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Search } from 'lucide-react';
import Navbar from '../Layout/Navbar';
import { getClients, createClient, deleteClient } from '../../api/client';

const PAGE_SIZE = 20;

export default function ClientSelector() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    async function load() {
      try {
        const data = await getClients();
        setClients(data);
      } catch (err) {
        console.error('Failed to load clients:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  function selectClient(client) {
    localStorage.setItem('activeClient', JSON.stringify(client));
    navigate('/dashboard');
  }

  async function handleCreateClient(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const client = await createClient(newName.trim());
      setClients((prev) => [...prev, client]);
      setCreating(false);
      setNewName('');
    } catch (err) {
      console.error('Failed to create client:', err);
    } finally {
      setSaving(false);
    }
  }

  async function handleBulkDelete() {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} client(s) and ALL their maps and users?\n\nThis cannot be undone.`)) return;
    let deleted = 0;
    for (const id of selected) {
      try {
        await deleteClient(id);
        deleted++;
      } catch (err) {
        console.error(`Failed to delete client ${id}:`, err);
      }
    }
    setSelected(new Set());
    setClients((prev) => prev.filter((c) => !selected.has(c.id)));
  }

  const filtered = clients
    .filter((c) => !search.trim() || c.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const safePage = Math.min(page, totalPages || 1);
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <Navbar />

      <main className="flex-1 px-6 py-6">
        <div className="w-full max-w-3xl mx-auto">
          {/* Header row */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-heading font-semibold text-txt-primary">
                Clients
              </h1>
              <span className="text-xs text-txt-secondary bg-gray-100 px-2 py-0.5 rounded-full">{clients.length}</span>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={sortBy}
                onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                className="rounded-lg border border-navy/10 text-sm py-1.5 pl-3 pr-8 focus:border-gold focus:ring-1 focus:ring-gold bg-white text-txt-secondary"
              >
                <option value="name">Sort: A to Z</option>
                <option value="date">Sort: Newest</option>
              </select>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-txt-secondary/50" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search clients..."
                  className="pl-8 pr-3 py-1.5 rounded-lg border border-navy/10 text-sm w-48
                    focus:border-gold focus:ring-1 focus:ring-gold bg-white placeholder:text-gray-400"
                />
              </div>
              {creating ? (
                <form onSubmit={handleCreateClient} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Client name"
                    autoFocus
                    className="rounded-lg border-gray-300 text-sm w-40 py-1.5 focus:border-gold focus:ring-gold"
                  />
                  <button type="submit" disabled={saving || !newName.trim()} className="btn-gold text-sm disabled:opacity-40">
                    {saving ? '...' : 'Add'}
                  </button>
                  <button type="button" onClick={() => { setCreating(false); setNewName(''); }}
                    className="text-xs text-txt-secondary hover:text-txt-primary">Cancel</button>
                </form>
              ) : (
                <button onClick={() => setCreating(true)} className="btn-gold text-sm">+ New Client</button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="text-center text-txt-secondary py-8">Loading...</div>
          ) : (
            <>
              {/* Bulk actions + pagination */}
              <div className="flex items-center justify-between mb-2 text-sm text-txt-secondary">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox"
                      checked={paginated.length > 0 && paginated.every((c) => selected.has(c.id))}
                      onChange={(e) => {
                        const next = new Set(selected);
                        paginated.forEach((c) => { e.target.checked ? next.add(c.id) : next.delete(c.id); });
                        setSelected(next);
                      }}
                      className="rounded border-gray-300 text-gold focus:ring-gold"
                    />
                    <span className="text-xs">Select page</span>
                  </label>
                  {selected.size > 0 && (
                    <button onClick={handleBulkDelete}
                      className="text-xs text-red-500 hover:text-red-700 font-semibold">
                      Delete {selected.size} selected
                    </button>
                  )}
                </div>
                {filtered.length > PAGE_SIZE && (
                  <div className="flex items-center gap-1">
                    <span className="text-xs mr-2">{(safePage - 1) * PAGE_SIZE + 1} to {Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
                    <button onClick={() => setPage(1)} disabled={safePage <= 1}
                      className="px-2 py-1 rounded border border-navy/10 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">&laquo;</button>
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}
                      className="px-2 py-1 rounded border border-navy/10 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">&lsaquo;</button>
                    <span className="px-2 py-1 text-txt-primary font-semibold text-xs">{safePage}/{totalPages}</span>
                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
                      className="px-2 py-1 rounded border border-navy/10 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">&rsaquo;</button>
                    <button onClick={() => setPage(totalPages)} disabled={safePage >= totalPages}
                      className="px-2 py-1 rounded border border-navy/10 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed">&raquo;</button>
                  </div>
                )}
              </div>

              {/* Client grid */}
              <div className="grid grid-cols-2 gap-2">
                {paginated.map((client) => (
                  <div
                    key={client.id}
                    className="flex items-center bg-white rounded-lg border border-gray-200
                      hover:border-gold hover:shadow-sm transition-all group"
                  >
                    <input type="checkbox"
                      checked={selected.has(client.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        const next = new Set(selected);
                        e.target.checked ? next.add(client.id) : next.delete(client.id);
                        setSelected(next);
                      }}
                      className="ml-3 rounded border-gray-300 text-gold focus:ring-gold flex-shrink-0"
                    />
                    <button
                      onClick={() => selectClient(client)}
                      className="flex-1 px-3 py-3 text-left"
                    >
                      <span className="text-sm font-semibold text-txt-primary group-hover:text-black">
                        {client.name}
                      </span>
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(
                          `Delete "${client.name}" and ALL their clarity maps?\n\nThis cannot be undone.`
                        )) return;
                        try {
                          await deleteClient(client.id);
                          setClients((prev) => prev.filter((c) => c.id !== client.id));
                          setSelected((prev) => { const next = new Set(prev); next.delete(client.id); return next; });
                        } catch (err) {
                          console.error('Failed to delete client:', err);
                        }
                      }}
                      className="px-3 py-3 text-gray-300 hover:text-red-500 transition-colors"
                      title="Delete client and all maps"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>

              {filtered.length === 0 && (
                <p className="text-center text-txt-secondary text-sm py-8">
                  {search.trim() ? 'No clients match your search.' : 'No clients yet.'}
                </p>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
